import { useEffect, useState } from 'react';
import {
  Anchor,
  Badge,
  Box,
  Button,
  Center,
  Group,
  Loader,
  Modal,
  PasswordInput,
  ScrollArea,
  Stack,
  Text,
  ThemeIcon,
  Title,
  UnstyledButton,
  useMantineColorScheme,
} from '@mantine/core';
import { PillTabList } from '@shared/ui/PillTabList';

function ThemeColorSchemeControl() {
  const { colorScheme, setColorScheme } = useMantineColorScheme();
  return (
    <PillTabList
      value={colorScheme}
      onChange={(val) => setColorScheme(val as 'light' | 'dark' | 'auto')}
      items={[
        { value: 'light', children: '浅色' },
        { value: 'dark', children: '深色' },
        { value: 'auto', children: '跟随系统' },
      ]}
    />
  );
}
import {
  IconChevronLeft,
  IconChevronRight,
  IconDeviceDesktop,
  IconDeviceMobile,
  IconInfoCircle,
  IconSettings,
  IconWorld,
} from '@tabler/icons-react';
import { fetchDeviceSessions, fetchSecurityEvents, fetchTrustedDevices, revokeAllSessions, revokeDeviceSession, revokeOtherSessions } from '@domain/auth/api';
import type { AuthSecurityEvent, TrustedDevice } from '@domain/auth/api';
import type { AuthDeviceSession, AuthSessionUser } from '@domain/auth/types';
import { LEGAL_URLS } from '@shared/config/legal';
import { BrandLogo } from '@shared/ui/BrandLogo';
import {
  ProfileSettingsForm,
  type ProfileDraft,
  type ProfileImageField,
} from '../../profile/components/ProfileSettingsForm';
import type { WorkspaceViewportMode } from '../hooks/useAdaptiveWorkspaceLayout';
import type { SettingsSection } from '../types/settings';
import type { CallHistoryEntry } from '@domain/call/types';
import { CallHistoryStage } from './CallHistoryStage';

type SettingsStageProps = {
  section: SettingsSection | null;
  viewportMode: WorkspaceViewportMode;
  currentUser: AuthSessionUser;
  profileDraft: ProfileDraft;
  fallbackAvatarUrl?: string;
  cdnBase: string;
  profileLoadState: 'idle' | 'loading' | 'saving' | 'error';
  profileError: string | null;
  profileDirty: boolean;
  profileUploadingField: ProfileImageField | null;
  onBack: () => void;
  onProfileChange: (field: keyof ProfileDraft, value: string) => void;
  onProfileImagePick: (field: ProfileImageField, file: File) => void;
  onSaveProfile: () => void;
  onResetProfile: () => void;
  apiBase: string;
  onRedial: (entry: CallHistoryEntry) => void;
};

function deviceName(session: AuthDeviceSession) {
  const platform = session.runtimePlatform.toLowerCase();
  if (platform === 'web') return 'Web';
  if (platform === 'android') return 'Android';
  if (platform === 'ios') return 'IOS';
  if (platform === 'windows') return 'Windows';
  if (platform === 'mac') return 'Mac';
  if (platform === 'linux') return 'Linux';
  return '登录设备';
}

function DevicePlatformIcon({ platform }: { platform: string }) {
  const common = { size: 21, stroke: 1.8 };
  if (platform === 'android' || platform === 'ios') {
    return <IconDeviceMobile {...common} />;
  }
  if (platform === 'web') return <IconWorld {...common} />;
  return <IconDeviceDesktop {...common} />;
}

function DeviceSessionsPanel({ apiBase }: { apiBase: string }) {
  const [sessions, setSessions] = useState<AuthDeviceSession[]>([]);
  const [state, setState] = useState<'loading' | 'ready' | 'error'>('loading');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [revokingSessionId, setRevokingSessionId] = useState<string | null>(null);
  const [events,setEvents]=useState<AuthSecurityEvent[]>([]);
  const [trustedDevices,setTrustedDevices]=useState<TrustedDevice[]>([]);
  const [bulkScope,setBulkScope]=useState<'others'|'all'|null>(null);
  const [bulkPassword,setBulkPassword]=useState('');

  const loadSessions = async (signal?: AbortSignal) => {
    const nextSessions = await fetchDeviceSessions(apiBase, signal);
    setSessions(nextSessions);
    const [nextEvents,nextTrusted]=await Promise.all([fetchSecurityEvents(apiBase),fetchTrustedDevices(apiBase)]);
    setEvents(nextEvents); setTrustedDevices(nextTrusted);
    setState('ready');
    setErrorMessage(null);
  };

  useEffect(() => {
    const controller = new AbortController();
    const load = async () => {
      try {
        await loadSessions(controller.signal);
      } catch (error) {
        if (!controller.signal.aborted) setState('error');
      }
    };
    void load();
    return () => {
      controller.abort();
    };
  }, [apiBase]);

  if (state === 'loading') {
    return <Center py={42}><Loader size="sm" /></Center>;
  }
  if (state === 'error') {
    return (
      <Stack align="flex-start" gap="sm" py="md">
        <Text size="sm" c="red">暂时无法获取登录设备</Text>
        <Button variant="default" size="xs" onClick={() => void loadSessions()}>重试</Button>
      </Stack>
    );
  }
  if (sessions.length === 0) {
    return <Text size="sm" c="dimmed" py="md">暂无设备记录</Text>;
  }

  const runBulk=async(scope:'others'|'all')=>{
    const password=bulkPassword;
    if(!password)return;
    setRevokingSessionId(scope); setErrorMessage(null);
    try { if(scope==='all'){await revokeAllSessions(password,apiBase); window.location.reload();} else {await revokeOtherSessions(password,apiBase); await loadSessions(); setBulkScope(null); setBulkPassword('');} }
    catch { setErrorMessage('身份验证或设备退出失败，请重试'); }
    finally { setRevokingSessionId(null); }
  };
  return (
    <Stack gap="xl">
      <Modal opened={bulkScope!==null} onClose={()=>{if(!revokingSessionId){setBulkScope(null);setBulkPassword('');setErrorMessage(null);}}} title={bulkScope==='all'?'退出全部设备':'退出其他设备'} centered>
        <Stack gap="md">
          <Text size="sm" c="dimmed">{bulkScope==='all'?'当前设备也会退出，所有设备都需要重新登录。':'当前设备保持登录，其他有效会话会立即撤销。'}</Text>
          <PasswordInput label="当前密码" value={bulkPassword} onChange={event=>setBulkPassword(event.currentTarget.value)} autoFocus />
          {errorMessage&&<Text size="sm" c="red">{errorMessage}</Text>}
          <Group justify="flex-end"><Button variant="default" onClick={()=>{setBulkScope(null);setBulkPassword('');}}>取消</Button><Button color="red" disabled={!bulkPassword} loading={revokingSessionId!==null} onClick={()=>bulkScope&&void runBulk(bulkScope)}>确认退出</Button></Group>
        </Stack>
      </Modal>
      <Stack gap={0}>
      <Group justify="space-between" pb="xs"><Text fw={800}>登录设备</Text><Group gap="xs"><Button size="xs" variant="light" onClick={()=>{setBulkScope('others');setErrorMessage(null);}}>退出其他设备</Button><Button size="xs" color="red" variant="light" onClick={()=>{setBulkScope('all');setErrorMessage(null);}}>退出全部设备</Button></Group></Group>
      {errorMessage && <Text size="sm" c="red" pb="sm">{errorMessage}</Text>}
      {sessions.map((session, index) => (
        <Group
          key={session.deviceId}
          justify="space-between"
          align="center"
          wrap="nowrap"
          py="md"
          style={index < sessions.length - 1 ? { borderBottom: '1px solid var(--locs-border-faint)' } : undefined}
        >
          <Group gap="md" wrap="nowrap" style={{ minWidth: 0 }}>
            <Box c="var(--locs-text-muted)" style={{ display: 'grid', placeItems: 'center' }}>
              <DevicePlatformIcon platform={session.runtimePlatform} />
            </Box>
            <Stack gap={2} style={{ minWidth: 0 }}>
              <Text size="sm" fw={700} truncate>
                {deviceName(session)}{session.isCurrent ? ' · 当前设备' : ''}
              </Text>
              <Text size="xs" c="dimmed" truncate>
                登录于 {new Date(session.createdAt * 1000).toLocaleString()}
              </Text>
            </Stack>
          </Group>
          {session.isCurrent ? (
            <Badge color="green" variant="light" size="sm">当前设备</Badge>
          ) : (
            <Button
              color="red"
              variant="subtle"
              size="xs"
              loading={revokingSessionId === session.sessionId}
              disabled={revokingSessionId !== null}
              onClick={async () => {
                setRevokingSessionId(session.sessionId);
                setErrorMessage(null);
                try {
                  await revokeDeviceSession(session.sessionId, apiBase);
                  await loadSessions();
                } catch {
                  setErrorMessage('退出设备失败，请稍后重试');
                } finally {
                  setRevokingSessionId(null);
                }
              }}
            >
              退出设备
            </Button>
          )}
        </Group>
      ))}
      </Stack>
      <Stack gap="xs"><Text fw={800}>可信设备</Text>{trustedDevices.length===0?<Text size="sm" c="dimmed">暂无可信设备</Text>:trustedDevices.map(device=><Group key={device.deviceId} justify="space-between"><Text size="sm">{device.displayName||'未命名设备'}</Text><Badge variant="light">{device.status}</Badge></Group>)}</Stack>
      <Stack gap="xs"><Text fw={800}>最近活动</Text>{events.length===0?<Text size="sm" c="dimmed">暂无活动</Text>:events.map(event=><Group key={event.eventId} justify="space-between" wrap="nowrap"><Stack gap={0}><Text size="sm" fw={650}>{event.eventType}</Text><Text size="xs" c="dimmed">{new Date(event.createdAt*1000).toLocaleString()}</Text></Stack>{event.notification&&<Badge variant="light" color={event.notification.status==='sent'?'green':'gray'}>{event.notification.status}</Badge>}</Group>)}</Stack>
    </Stack>
  );
}

function SettingsStageHeader({
  title,
  viewportMode,
  onBack,
}: {
  title: string;
  viewportMode: WorkspaceViewportMode;
  onBack: () => void;
}) {
  return (
    <Box
      px={{ base: 'md', md: 'xl' }}
      py="md"
      style={{ background: 'transparent' }}
    >
      <Group justify="space-between" wrap="nowrap">
        {viewportMode !== 'desktop' ? (
          <UnstyledButton
            onClick={onBack}
            aria-label="返回设置列表"
            p={8}
            style={{ borderRadius: 'var(--mantine-radius-md)', color: 'var(--locs-text-muted)' }}
          >
            <IconChevronLeft size={20} stroke={2} />
          </UnstyledButton>
        ) : <Box w={36} />}
        <Text fw={850} size="md" ff="var(--mantine-font-family-headings)">{title}</Text>
        <Box w={36} />
      </Group>
    </Box>
  );
}

function SettingsValueRow({ label, value }: { label: string; value: string }) {
  return (
    <Group justify="space-between" gap="lg" px="md" py={14} wrap="nowrap">
      <Text size="sm" fw={650}>{label}</Text>
      <Text size="sm" c="dimmed" truncate ta="right">{value}</Text>
    </Group>
  );
}

function SettingsLinkRow({ label, href }: { label: string; href: string }) {
  return (
    <Anchor href={href} target="_blank" rel="noreferrer" underline="never" c="inherit">
      <Group justify="space-between" gap="lg" px="md" py={14} wrap="nowrap">
        <Text size="sm" fw={650}>{label}</Text>
        <IconChevronRight size={16} color="var(--locs-text-muted)" />
      </Group>
    </Anchor>
  );
}

function SettingsRowGroup({ children }: { children: React.ReactNode }) {
  return <Stack gap={4}>{children}</Stack>;
}

export function SettingsStage({
  section,
  viewportMode,
  currentUser,
  profileDraft,
  fallbackAvatarUrl,
  cdnBase,
  profileLoadState,
  profileError,
  profileDirty,
  profileUploadingField,
  onBack,
  onProfileChange,
  onProfileImagePick,
  onSaveProfile,
  onResetProfile,
  apiBase,
  onRedial,
}: SettingsStageProps) {
  const sectionTitle = {
    profile: '个人资料',
    callHistory: '通话记录',
    appearance: '外观与主题',
    connection: '设备与安全',
    privacy: '隐私条款',
    about: '关于 LocsClub',
  }[section ?? 'profile'];

  return (
    <Stack gap={0} h="100%" bg="var(--locs-canvas)">
      <SettingsStageHeader title={section ? sectionTitle : '设置'} viewportMode={viewportMode} onBack={onBack} />
      <ScrollArea flex={1} offsetScrollbars>
        <Box maw={820} mx="auto" px={{ base: 'md', sm: 'xl' }} py={{ base: 'lg', sm: 38 }}>
          {!section && (
            <Stack align="center" gap="md" py={70} ta="center">
              <ThemeIcon size={72} radius="xl" variant="light" color="terracotta">
                <IconSettings size={34} stroke={1.8} />
              </ThemeIcon>
              <Title order={2} fw={850}>设置</Title>
              <Text c="dimmed" size="sm" maw={420} lh={1.6}>
                从设置列表中选择一个项目，管理个人资料、连接状态、隐私信息与客户端详情
              </Text>
            </Stack>
          )}

          {section === 'profile' && (
            <ProfileSettingsForm
              currentUser={currentUser}
              draft={profileDraft}
              fallbackAvatarUrl={fallbackAvatarUrl}
              loadState={profileLoadState}
              error={profileError}
              dirty={profileDirty}
              cdnBase={cdnBase}
              uploadingField={profileUploadingField}
              onChange={onProfileChange}
              onPickImage={onProfileImagePick}
              onSave={onSaveProfile}
              onReset={onResetProfile}
            />
          )}

          {section === 'callHistory' && (
            <CallHistoryStage apiBase={apiBase} onRedial={onRedial} />
          )}

          {section === 'appearance' && (
            <Group justify="space-between" align="center" gap="lg" py="md" wrap="wrap">
              <Text size="sm" fw={700}>主题色</Text>
              <ThemeColorSchemeControl />
            </Group>
          )}

          {section === 'connection' && (
            <DeviceSessionsPanel apiBase={apiBase} />
          )}

          {section === 'privacy' && (
            <SettingsRowGroup>
              <SettingsLinkRow label="隐私政策" href={LEGAL_URLS.privacyPolicy} />
              <SettingsLinkRow label="服务条款" href={LEGAL_URLS.termsOfService} />
              <SettingsLinkRow label="安全中心" href={LEGAL_URLS.safetyCenter} />
            </SettingsRowGroup>
          )}

          {section === 'about' && (
            <Stack align="center" gap="lg" py={34} ta="center">
              <BrandLogo size={76} />
              <Stack gap={4}>
                <Title order={2} fw={900}>Locs.Club</Title>
              </Stack>
              <Box w="100%" maw={520}>
                <SettingsRowGroup>
                  <SettingsValueRow label="客户端" value="Web" />
                  <SettingsValueRow label="版本" value="0.1.0" />
                </SettingsRowGroup>
              </Box>
              <Group gap="xs" c="dimmed">
                <IconInfoCircle size={16} />
                <Text size="xs">Locs.Club Web Client</Text>
              </Group>
            </Stack>
          )}
        </Box>
      </ScrollArea>
    </Stack>
  );
}
